import {ILoginMethod, IUser} from '../../../auth/type';
import {IS_CLIENT, NEED_TRACKING, REGION} from '../../constant';
import {getDeviceId, getKakaoAppKey} from '../../function';
import {getClientStore} from '../../store';
import {IRegion} from '../../type';
import {assert} from '../assert';

/**
 * Tiara 연동 가이드: https://wiki.daumkakao.com/display/developerguide/WEB
 * 로그 정의서: https://docs.google.com/spreadsheets/d/1q0Luofiqp9RFMLxvgciy83kCdf69ySfKSUps98Opdw4/edit#gid=1738196477
 */

const SVC_DOMAIN_MAP: {[region in IRegion]: string} = {
  kor: 'webtoon.kakao.com',
  twn: 'tw.kakaowebtoon.com',
  tha: 'th.kakaowebtoon.com',
  idn: 'id.kakaowebtoon.com',
};

const config = {
  svcDomain: SVC_DOMAIN_MAP[REGION],
  deployment: NEED_TRACKING ? 'production' : 'dev',
  enableAlwaysHttpPost: true, // 우리쪽 서버에서 받아야해서 POST로 고정해서 사용
  sessionTimeout: '1800',
};

interface ITrackParamCommon {
  user?: IUser;
  section: string;
  page: string; // 유저가 하나의 화면으로 인지하는 단위 (예: main_recommended)
  customProps?: {[key: string]: any};
}

export interface ITrackPageViewParam extends ITrackParamCommon {
  actionName: string;
}

export type ITrackExtraParam = ITrackPageViewParam;

type IEventAction =
  | 'click'
  | 'swipe'
  | 'longpress'
  | 'play'
  | 'open'
  | 'start'
  | 'complete'
  | 'scroll';

export interface ITrackEventParam extends ITrackParamCommon {
  actionName: string;
  action: IEventAction;
  kind?: 'Purchase'; // https://wiki.daumkakao.com/display/developerguide/ActionKind에 정의된 값만 가능
  ecommerceContents?: {[key: string]: any};
}

export interface ITrackViewImpParam extends ITrackParamCommon {
  viewImpContents?: {[key: string]: any};
}
export interface ITrackUsageParam extends ITrackParamCommon {}

export let isInit = false;
let isInitializing = false;
export function init() {
  assert(IS_CLIENT, '클라이언트에서 실행되어야합니다.');

  if (isInitializing || isInit) return;
  isInitializing = true;

  // @ts-ignore
  import('../../../common/util/tracking/tiara/1.1.16/tiara.js')
    .then(() => {
      window.TiaraTracker.getInstance().init(config);
      isInit = true;
    })
    .finally(() => {
      isInitializing = false;
    });
}

export const prevPageView = {section: '', page: ''};
let prevEvent: {page: string; actionName: string; userAction: string};
let prevUsedEvent: typeof prevEvent | undefined;
let conversionData:
  | {
      torosPageMetaId: string | undefined;
      contentId: number | undefined;
      torosImpId: string | undefined;
      torosImpOpt: string | undefined;
      torosFileHashKey: string | undefined;
    }
  | undefined;
export const readingSwitchData: {
  customProps?: {
    last_click_section: string;
    last_click_page: string;
    last_click_action_name: string;
    last_click_content_id: number;
    last_click_section_id?: string;
    last_click_cardgroup_id?: string;
    last_click_placement?: string;
    last_click_ordnum?: number;
  };
} = {};

export function trackPageView({
  user,
  section,
  page,
  actionName,
  customProps,
}: ITrackPageViewParam) {
  presetTracker(user)
    .setSection(section)
    .setPage(page)
    .trackPage(actionName)
    .customProps(
      addCommonProps(
        {
          ...customProps,
          user_action: 'pv',
          // 발생로그 직전 이벤트가 pageview, click, swipe인 경우 전송
          before_page_name: prevEvent?.page,
          before_click_name: prevEvent?.actionName,
        },
        user
      )
    )
    .track();

  prevUsedEvent = prevEvent;
  prevEvent = {page, actionName, userAction: 'pv'};
  prevPageView.section = section;
  prevPageView.page = page;

  if (!getIsConversionPage({section, page, customProps})) {
    conversionData = undefined;
  }
  if (getIsNotReadingSwitchPage({section, customProps})) {
    readingSwitchData.customProps = undefined;
  }
}

// 참고: https://podotree.atlassian.net/wiki/spaces/DD/pages/2334163175
function getIsConversionPage({
  section,
  page,
  customProps,
}: Pick<ITrackPageViewParam, 'section' | 'page' | 'customProps'>) {
  if (section === 'content_home') {
    if (
      page !== 'content_keyword_list' &&
      (page !== 'content_home' || // 첫 화면에서 작품id가 다르면 reset 되기 때문에 다른 하위 페이지에서는 검사하지 않는다.
        conversionData?.contentId === customProps?.content_id)
    ) {
      return true;
    }
    // 이벤트 댓글 모달도 동일하지만, 이벤트 페이지 진입시 reset 되기 때문에 괜찮다.
  } else if (section === 'viewer' || section === 'comments') {
    return true;
  }

  return false;
}
function getIsNotReadingSwitchPage({
  section,
  customProps,
}: Pick<ITrackPageViewParam, 'section' | 'customProps'>) {
  if (section === 'content_home' || section === 'viewer') {
    // 작품홈, 뷰어 content_id 비교.
    return (
      !readingSwitchData.customProps ||
      !customProps ||
      readingSwitchData.customProps.last_click_content_id !==
        customProps.content_id
    );
  }
  return false;
}

// pageview와 동일하나 추가 데이터(진입 이후에 api로 받아와야 하는) 전송을 위해 사용
export function trackExtra({
  user,
  section,
  page,
  actionName,
  customProps,
}: ITrackPageViewParam) {
  presetTracker(user)
    .setSection(section)
    .setPage(page)
    .trackExtra(actionName)
    .customProps(
      addCommonProps(
        {
          ...customProps,
          user_action: 'pv',
          // pageview와 동일한 값을 전송
          before_page_name: prevUsedEvent?.page,
          before_click_name: prevUsedEvent?.actionName,
        },
        user
      )
    )
    .track();

  prevUsedEvent = undefined;
}

const SERVICE_FILE_HASH_KEY: {[region in IRegion]?: string} = {
  twn: '89ef56e6afaa50dca5',
  tha: '0790874b21dff91f95',
  kor: '15e097b9fcf25cbbd7',
  idn: '', // TODO: IDN
};
export function trackEvent({
  user,
  section,
  page,
  actionName,
  action,
  kind,
  ecommerceContents,
  customProps,
}: ITrackEventParam) {
  if (actionName === 'read' && action === 'start') {
    customProps ??= {};
    if (conversionData) {
      customProps.toros_action_type = 'conversion';
      customProps.toros_page_meta_id = conversionData.torosPageMetaId;
      customProps.toros_imp_id = conversionData.torosImpId;
      customProps.toros_imp_opt = conversionData.torosImpOpt;
      customProps.toros_file_hash_key = conversionData.torosFileHashKey;
    } else {
      customProps.toros_file_hash_key = SERVICE_FILE_HASH_KEY[REGION];
    }

    if (readingSwitchData.customProps) {
      customProps = {
        ...customProps,
        ...readingSwitchData.customProps,
      };
    }
  }

  section = section.replace('{{section}}', prevPageView.section);
  page = page.replace('{{page}}', prevPageView.page);

  presetTracker(user)
    .setSection(section)
    .setPage(page)
    .trackEvent(actionName)
    .actionKind(kind ?? '')
    .ecommerceContents(
      ecommerceContents ? [getValidCustomProps(ecommerceContents)] : undefined
    )
    .customProps(addCommonProps({...customProps, user_action: action}, user))
    .track();

  if (action === 'click' || action === 'swipe') {
    prevEvent = {page, actionName, userAction: action};
  }

  // 추천 작품 클릭인 경우
  if (action === 'click' && !!customProps?.toros_imp_id) {
    conversionData = {
      torosPageMetaId: customProps.toros_page_meta_id,
      contentId: customProps.toros_event_meta_id,
      torosImpId: customProps.toros_imp_id,
      torosImpOpt: customProps.toros_imp_opt,
      torosFileHashKey: customProps.toros_file_hash_key,
    };
  }
}

export function trackViewImp({
  user,
  section = '{{section}}',
  page = '{{page}}',
  customProps,
  viewImpContents,
}: ITrackViewImpParam) {
  section = section.replace('{{section}}', prevPageView.section);
  page = page.replace('{{page}}', prevPageView.page);

  presetTracker(user)
    .setSection(section)
    .setPage(page)
    .trackViewImp()
    .customProps(addCommonProps({...customProps, user_action: 'imp'}, user))
    .viewImpContents(viewImpContents ? [viewImpContents] : undefined)
    .track();
}

export function trackUsage({
  user,
  section,
  page,
  customProps,
}: ITrackUsageParam) {
  presetTracker(user)
    .setSection(section)
    .setPage(page)
    .trackUsage()
    .customProps(addCommonProps({...customProps, user_action: 'none'}, user)) // TODO: user_action 미정
    .usage() // TODO: 티아라에서는 duration, scroll_percent 등을 넣는다. 우리쪽엔 아직 정의가 되어 있지 않다.
    .track();
}

function addCommonProps(customProps: {[key: string]: any}, user?: IUser) {
  return {
    ...getValidCustomProps(customProps),
    region: REGION,
    user_language: getClientStore()?.getState().common.lang,
    islogin: user ? 'Y' : 'N',
  };
}

function presetTracker(user?: IUser) {
  const instance = window.TiaraTracker.getInstance();

  // SPA에서는 갱신이 안되서 매번 설정해야 한다.
  instance.setUrl(document.location.href).setTitle(document.title);
  // .setReferrer(window.document.referrer) // TODO: referrer를 별도로 가져와야 함

  const exAccount: {[key: string]: any} = {islogin: user ? 'Y' : 'N'};
  if (user) {
    instance.setThirdProvideAgree(true).setThirdAdAgree(user.isThirdAdAgree);
    if (user.kakaoAppUserId) {
      instance
        .setAppUserId(user.kakaoAppUserId)
        .setKakaoAppKey(getKakaoAppKey());
    }
    exAccount.user_id = user.id;
    exAccount.provider = TRACKING_PROVIDER[user.joinMethod];
    exAccount.provider_id = user.providerId;
  }

  instance.userExAccount(exAccount).userExAnonymous({
    app_id: getDeviceId(),
  });

  return instance;
}

function getValidCustomProps(obj?: {[key: string]: any}) {
  if (!obj) {
    return {};
  }
  const prop: {[key: string]: any} = {};
  Object.keys(obj).forEach(k => {
    prop[k] = typeof obj[k] === 'number' ? String(obj[k]) : obj[k];
  });
  return prop;
}

const TRACKING_PROVIDER: {[key in ILoginMethod]: string} = {
  FB: 'facebook',
  GG: 'google',
  AP: 'apple',
  LN: 'line',
  KA: 'kakaotalk',
  DA: 'daum',
};
